<template>
  <div>
    <div v-for="(rows, style) in groupedRows" :key="style" class="mb-6 avoid-break">
      <h3 class="mb-2 text-base font-bold">
        {{ rows[0].modelName }} (Style {{ rows[0].style }})
      </h3>
      <table class="w-full text-xs mt-2 border-collapse" style="table-layout:fixed">
        <thead>
          <tr>
            <th class="text-left p-1 w-[18%]">Color</th>
            <th class="text-left p-1 w-[12%]">Size</th>
            <th class="text-left p-1 w-[40%]">SKU</th>
            <th class="text-center p-1 w-[10%]">Qty</th>
            <th class="text-right p-1 w-[20%]">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.sku">
            <td class="p-1 truncate">{{ row.colour }}</td>
            <td class="p-1 truncate">{{ row.size }}</td>
            <td class="p-1 truncate">{{ row.sku }}</td>
            <td class="text-center p-1">{{ row.qty }}</td>
            <td class="text-right p-1">{{ formatNumber(row.totalPrice, currency) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="text-right pr-4 py-2">Total for style:</td>
            <td class="text-right py-2">{{ formatNumber(styleTotals[style], currency) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <table class="w-full text-xs mt-2 border-collapse" style="table-layout:fixed">
      <tfoot>
        <tr>
          <td colspan="4" class="text-right pr-4 py-2 font-semibold">Total for order ({{ totalProductCount }} pcs):</td>
          <td class="text-right py-2 font-bold">{{ formatNumber(orderTotal, currency) }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatNumber } from '@/utils/formatNumber'
import { sizeSortIndex } from '@/utils/sizes'

const props = defineProps<{
  rows: any[],
  currency: string
}>()

const groupedRows = computed(() => {
  const grouped: Record<string, any[]> = {}
  for (const row of props.rows) {
    if (!grouped[row.style]) grouped[row.style] = []
    grouped[row.style].push(row)
  }
  // Sort rows within style by modelName, colour, size
  for (const style in grouped) {
    grouped[style] = grouped[style].sort((a, b) => {
      if (a.modelName !== b.modelName) return String(a.modelName).localeCompare(String(b.modelName))
      if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
      return sizeSortIndex(a.size) - sizeSortIndex(b.size)
    })
  }
  // Sort styles by model name
  const sortedStyles = Object.keys(grouped).sort((a, b) => {
    const nameA = grouped[a][0]?.modelName || ''
    const nameB = grouped[b][0]?.modelName || ''
    return String(nameA).localeCompare(String(nameB))
  })
  // Build sorted object
  const out: Record<string, any[]> = {}
  for (const style of sortedStyles) out[style] = grouped[style]
  return out
})

const styleTotals = computed(() => {
  const totals: Record<string, number> = {}
  for (const style in groupedRows.value) {
    totals[style] = groupedRows.value[style].reduce((sum, row) => sum + row.totalPrice, 0)
  }
  return totals
})

const orderTotal = computed(() =>
  Object.values(groupedRows.value).flat().reduce((sum, row) => sum + row.totalPrice, 0)
)

const totalProductCount = computed(() =>
  Object.values(groupedRows.value).flat().reduce((sum, row) => sum + row.qty, 0)
)
</script>

<style scoped>
table {
  table-layout: fixed;
}
th, td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>